import { inject, Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { jwtDecode } from 'jwt-decode';
import { firstValueFrom } from 'rxjs';

interface AccessTokenPayload {
  scope?: string;
}

/**
 * The three `read:`/`update:`/`review:community_club_onboarding` permissions this dashboard
 * gates on are real Auth0 API scopes carried in the `scope` claim of the GraphQL access token -
 * NOT the `userRoles` custom claim the management-portal uses for its business-owner roles
 * (this dashboard has no company-scoped roles, only internal reviewer permissions). The backend
 * enforces these scopes independently via `@Scope(...)` guards, so decoding the token here
 * client-side without verifying its signature is safe: it only ever gates UI, never data access.
 */
@Injectable({ providedIn: 'root' })
export class ScopeService {
  private readonly _auth = inject(AuthService);

  private _scopes = new Set<string>();

  async loadScopes(): Promise<void> {
    try {
      const token = await firstValueFrom(this._auth.getAccessTokenSilently());
      const decoded = jwtDecode<AccessTokenPayload>(token);
      this._scopes = new Set((decoded.scope ?? '').split(' ').filter(Boolean));
    } catch {
      this._scopes = new Set();
    }
  }

  hasScope(scope: string): boolean {
    return this._scopes.has(scope);
  }

  hasAnyScope(scopes: string[]): boolean {
    return scopes.some(scope => this._scopes.has(scope));
  }
}
